import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { PrescriptionDetailsModal } from '../components/prescription/PrescriptionDetailsModal';
import { PrescriptionSuccessModal } from '../components/prescription/PrescriptionSuccessModal';
import { PrescriptionCancelBottomSheet } from '../components/prescription/PrescriptionCancelBottomSheet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trackPrescriptionCashOrder, trackPrescriptionInsuranceOrder, trackPrescriptionWasfatyOrder, trackPrescriptionExternalOrder, trackPrescriptionPageView } from '../lib/analytics/events';
import { MapPinIcon, EditIcon, FileTextIcon, ImageIcon, CreditCardIcon, WalletIcon, BanknoteIcon, CheckCircleIcon, ClockIcon, TruckIcon, XCircleIcon, PackageIcon, ChevronRightIcon, HomeIcon, InfoIcon } from 'lucide-react';
import { prescriptionRecords } from '../data/prescriptionRecords';
type Beneficiary = {
  id: 'self' | 'sara';
  name: string;
  detail: string;
};
const beneficiaries: Beneficiary[] = [{
  id: 'self',
  name: 'Ahmed Mohammed',
  detail: 'Me · Bupa Arabia'
}, {
  id: 'sara',
  name: 'Sara Mohammed',
  detail: 'Daughter · Bupa Arabia'
}];
export function PrescriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPrescriptionType = searchParams.get('type');
  const [selectedPrescriptionType, setSelectedPrescriptionType] = useState(initialPrescriptionType === 'insurance' || initialPrescriptionType === 'wasfaty' || initialPrescriptionType === 'cash' || initialPrescriptionType === 'outpatient' || initialPrescriptionType === 'yusr' ? initialPrescriptionType : '');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedOutOfStock, setSelectedOutOfStock] = useState('');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary['id'] | ''>('');
  const [prescriptionDetails, setPrescriptionDetails] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [wasfatyCustomerName, setWasfatyCustomerName] = useState('');
  const [wasfatyNationalId, setWasfatyNationalId] = useState('');
  const [wasfatyNumber, setWasfatyNumber] = useState('');
  const [insuranceCustomerName, setInsuranceCustomerName] = useState('');
  const [insuranceNationalId, setInsuranceNationalId] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [outpatientClinic, setOutpatientClinic] = useState('');
  const [yusrReference, setYusrReference] = useState('');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const fulfillmentMethod = 'delivery' as const;
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const recentPrescriptions = prescriptionRecords;
  const isFormValid = () => {
    const baseValid = selectedBeneficiary && selectedPrescriptionType && (prescriptionDetails || selectedFile) && selectedPaymentMethod && selectedOutOfStock;
    if (!baseValid) return false;
    if (selectedPrescriptionType === 'wasfaty') {
      return wasfatyCustomerName && wasfatyNationalId && wasfatyNumber;
    }
    if (selectedPrescriptionType === 'insurance') {
      return insuranceCustomerName && insuranceNationalId && insuranceProvider;
    }
    if (selectedPrescriptionType === 'outpatient') return outpatientClinic;
    if (selectedPrescriptionType === 'yusr') return yusrReference;
    return true;
  };
  // Determine next required step (label + section id to scroll to)
  const getNextRequiredStep = (): {
    label: string;
    sectionId: string;
  } | null => {
    if (!selectedBeneficiary) {
      return {
        label: 'Choose who this prescription is for',
        sectionId: 'section-beneficiary'
      };
    }
    if (!selectedPrescriptionType) {
      return {
        label: 'Select Prescription Type',
        sectionId: 'section-prescription-type'
      };
    }
    if (selectedPrescriptionType === 'wasfaty') {
      if (!wasfatyCustomerName || !wasfatyNationalId || !wasfatyNumber) {
        return {
          label: 'Complete Wasfaty Information',
          sectionId: 'section-prescription-type'
        };
      }
    }
    if (selectedPrescriptionType === 'insurance') {
      if (!insuranceCustomerName || !insuranceNationalId || !insuranceProvider) return {
        label: 'Complete Insurance Information',
        sectionId: 'section-prescription-type'
      };
    }
    if (selectedPrescriptionType === 'outpatient' && !outpatientClinic) return {
      label: 'Add Clinic Information',
      sectionId: 'section-prescription-type'
    };
    if (selectedPrescriptionType === 'yusr' && !yusrReference) return {
      label: 'Add Yusr Reference',
      sectionId: 'section-prescription-type'
    };
    if (!prescriptionDetails && !selectedFile) {
      return {
        label: 'Upload Prescription',
        sectionId: 'section-upload'
      };
    }
    if (!selectedPaymentMethod) {
      return {
        label: 'Select Payment Method',
        sectionId: 'section-payment'
      };
    }
    if (!selectedOutOfStock) {
      return {
        label: 'Choose Out-of-Stock Option',
        sectionId: 'section-out-of-stock'
      };
    }
    return null;
  };
  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const headerOffset = window.innerWidth >= 768 ? 220 : 120;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
    // Add a brief highlight pulse
    el.classList.add('ring-2', 'ring-[#1E2B55]', 'ring-offset-2');
    setTimeout(() => {
      el.classList.remove('ring-2', 'ring-[#1E2B55]', 'ring-offset-2');
    }, 1500);
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-8315" data-mp="1tsefzj08i3c1" />;
      case 'ready':
        return <PackageIcon className="w-4 h-4 text-blue-500" data-id="element-8316" data-mp="1otl763vw70i7" />;
      case 'in-progress':
      case 'expired':
        return <ClockIcon className="w-4 h-4 text-yellow-500" data-id="element-8317" data-mp="23rdon1l01yz1" />;
      case 'insurance-rejected':
      default:
        return <XCircleIcon className="w-4 h-4 text-red-500" data-id="element-8318" data-mp="1yrhk918e45iy" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready';
      case 'in-progress':
        return 'In Progress';
      case 'expired':
        return 'Prescription expired';
      case 'insurance-rejected':
        return 'Insurance claim rejected';
      default:
        return 'Rejected';
    }
  };
  const handlePrescriptionClick = (prescription: any) => {
    setSelectedPrescription(prescription);
    setIsModalOpen(true);
  };
  const handleViewAllClick = () => {
    navigate('/prescription/history');
  };
  const handleViewHistoryClick = () => {
    // Track analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;
      (window as any).gtag('event', 'prescription_history_click', {
        page: 'prescription',
        device: 'mobile'
      });
    }
    navigate('/prescription/history');
  };
  // Track prescription page view on mount
  useEffect(() => {
    if (selectedPrescriptionType) {
      trackPrescriptionPageView(selectedPrescriptionType as 'cash' | 'insurance' | 'wasfaty' | 'outpatient' | 'yusr', 'start');
    }
  }, [selectedPrescriptionType]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('Please complete all required fields', {
        duration: 5000
      });
      return;
    }
    if (!selectedFile && !prescriptionDetails) {
      toast.error('Please upload prescription file or enter details', {
        duration: 5000
      });
      return;
    }
    setIsSubmitting(true);
    // Track analytics based on prescription type
    // Generate a reference number and record the selected prescription flow.
    const generatedReference = `#RX-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 89999)}`;
    if (selectedPrescriptionType === 'cash') {
      trackPrescriptionCashOrder(generatedReference, 0, fulfillmentMethod);
    } else if (selectedPrescriptionType === 'insurance') {
      trackPrescriptionInsuranceOrder(generatedReference, 0, insuranceProvider, fulfillmentMethod);
    } else if (selectedPrescriptionType === 'wasfaty') {
      trackPrescriptionWasfatyOrder(generatedReference, 0, fulfillmentMethod);
    } else if (selectedPrescriptionType === 'outpatient' || selectedPrescriptionType === 'yusr') {
      trackPrescriptionExternalOrder(selectedPrescriptionType, generatedReference, fulfillmentMethod);
    }
    // Generate a reference number and show the detailed success state
    setReferenceNumber(generatedReference);
    setShowSuccessModal(true);
    setIsSubmitting(false);
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-8319" data-mp="u5c2uxl9c5vr">
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-8320" data-mp="p68ylfd3q3ou">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-8321" data-mp="135idmbpkmrr8">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-8322" data-mp="136jr0mu27qc4" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-8323" data-mp="183to8onox6ai">
          <h1 className="sr-only" data-id="element-8324" data-mp="1msg1mktrbnz3">
            Upload Your Prescription - Fast & Secure Medicine Delivery
          </h1>
          {/* Mobile Header Overlay */}
          <MobileInternalHeader title="Prescription Services" data-id="element-8325" data-mp="1vu60uctvrr7f" />
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Prescription Services'
        }]} title="Prescription Services" description="Upload your prescription and get your medications delivered quickly and securely." data-id="element-8326" data-mp="1qvutc8r25ssd" />
          <div className="container mx-auto px-4 md:px-8 lg:px-12 py-8" data-id="element-8327" data-mp="25hibjodpan8l">
            {/* View Prescription History Button - Mobile Only */}
            <div className="md:hidden mb-6" data-id="element-8328" data-mp="1rpbyib5ugwdd">
              <button onClick={handleViewHistoryClick} className="w-full bg-white border-2 border-[#1E2B55] text-[#1E2B55] font-bold py-3 px-4 rounded-lg hover:bg-[#1E2B55] hover:text-white transition-all flex items-center justify-center gap-2" aria-label="Open prescription history" data-id="element-8329" data-mp="1wh3ho0mo7zth">
                <FileTextIcon className="w-5 h-5" data-id="element-8330" data-mp="18sar6jotik69" />
                View Prescription History
              </button>
            </div>
            <div className="flex flex-col lg:flex-row gap-6" data-id="element-8331" data-mp="3fg1boekyklg6">
              {/* Sidebar */}
              <div className="lg:w-80 flex-shrink-0 space-y-6" data-id="element-8332" data-mp="3fhw0i3zcoxpj">
                {/* How It Works */}
                <div className="bg-white rounded-xl shadow-md p-6 sticky top-24 hidden md:block" data-id="element-8333" data-mp="3ani1zspe6agn">
                  <h3 className="text-xl font-bold mb-6 text-center" data-id="element-8334" data-mp="35nvorj9kcu7u">
                    How It Works
                  </h3>
                  <div className="space-y-6" data-id="element-8335" data-mp="2a17q37upvig">
                    <div className="flex items-start" data-id="element-8336" data-mp="28q5w8xyuxtz">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-8337" data-mp="3tfkjt7drrwsh">
                        1
                      </div>
                      <div className="ml-4" data-id="element-8338" data-mp="3kfnssz3wqlu9">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-8339" data-mp="3keutgj7a2ym5">
                          Upload Prescription
                        </h4>
                        <p className="text-xs text-gray-600" data-id="element-8340" data-mp="2908spmgejdux">
                          Take a clear photo or scan your prescription
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-8341" data-mp="tg872b43t0i0">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-8342" data-mp="tex5mhy4s4u2">
                        2
                      </div>
                      <div className="ml-4" data-id="element-8343" data-mp="12bfvflb2fcmf">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-8344" data-mp="1797lsaw2aqoi">Verification</h4>
                        <p className="text-xs text-gray-600" data-id="element-8345" data-mp="1c7958wxegs0i">
                          Our pharmacist reviews and verifies your prescription
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-8346" data-mp="1l8h2rcs3dotj">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-8347" data-mp="1l5ut97ln4z8o">
                        3
                      </div>
                      <div className="ml-4" data-id="element-8348" data-mp="3n1dllls8tvo7">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-8349" data-mp="2rocgpik1g2p7">
                          Order Confirmation
                        </h4>
                        <p className="text-xs text-gray-600" data-id="element-8350" data-mp="2nnipaf7e05rk">
                          Receive confirmation and proceed to checkout
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-8351" data-mp="2wmnqxue8wf7y">
                      <div className="bg-[#1E2B55] text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold" data-id="element-8352" data-mp="28znzb6krkfi6">
                        4
                      </div>
                      <div className="ml-4" data-id="element-8353" data-mp="2ipqyjoihapoy">
                        <h4 className="font-bold mb-1 text-sm" data-id="element-8354" data-mp="2iqioojtb3v7u">Delivery</h4>
                        <p className="text-xs text-gray-600" data-id="element-8355" data-mp="2dwo4lf9she88">
                          Get your medicines delivered to your doorstep
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Recent Prescriptions */}
                <div className="bg-white rounded-xl shadow-md p-6 hidden md:block" data-id="element-8356" data-mp="pyapcc73s10">
                  <div className="flex items-center justify-between mb-4" data-id="element-8357" data-mp="5jkdmcsk4hjm">
                    <h3 className="text-lg font-bold" data-id="element-8358" data-mp="3nx0brlvknyoc">Recent Prescriptions</h3>
                    <button onClick={handleViewAllClick} className="text-xs font-medium text-[#1E2B55] hover:underline" data-id="element-8359" data-mp="3rx2fe0kvb269">
                      View All
                    </button>
                  </div>
                  <div className="space-y-3" data-id="element-8360" data-mp="oehilqrc22sk">
                    {recentPrescriptions.slice(0, 3).map(prescription => <button key={prescription.id} onClick={() => handlePrescriptionClick(prescription)} className="w-full border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-[#1E2B55] transition-all text-left" data-id="element-8361" data-mp="ffx6j8ga2rdb">
                        <div className="flex items-center justify-between mb-2" data-id="element-8362" data-mp="15p4l9a17652b">
                          <span className="text-xs font-bold text-gray-900" data-id="element-8363" data-mp="10ql0hcycazbj">
                            {prescription.id}
                          </span>
                          <div className="flex items-center" data-id="element-8364" data-mp="vy9zs7vrz9hx">
                            {getStatusIcon(prescription.status)}
                            <span className="text-xs ml-1" data-id="element-8365" data-mp="mzet6uotjxe3">
                              {getStatusText(prescription.status)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2" data-id="element-8366" data-mp="d0qoexluhesc">
                          {new Date(prescription.date).toLocaleDateString()}
                        </p>
                        <div className="text-xs text-gray-500" data-id="element-8367" data-mp="27fjekh3yldcw">
                          {prescription.items.length} item(s)
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>
              {/* Main Form */}
              <div className="flex-1 space-y-6" data-id="element-8368" data-mp="3qae8mu15dg29">
                <section id="section-fulfillment" className="scroll-mt-32 rounded-xl border border-[#b8e2df] bg-[#f2fbfa] p-4 md:p-6" aria-labelledby="fulfillment-title" data-id="element-8369" data-mp="3q9342rzvo4a3">
                  <div className="flex items-start gap-3" data-id="element-8370" data-mp="2xtr61hhukth">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand text-white" data-id="element-8371" data-mp="2zejdkgc5ro6"><TruckIcon className="h-5 w-5" aria-hidden="true" data-id="element-8372" data-mp="32f1rusxqmvdg" /></span>
                    <div className="min-w-0 flex-1" data-id="element-8373" data-mp="37c1xj2qodw41"><h2 id="fulfillment-title" className="text-xl font-bold text-gray-900" data-id="element-8374" data-mp="3bddi2tuozr5d">Home delivery</h2><p className="mt-1 text-sm text-gray-600" data-id="element-8375" data-mp="3bfq35fvqyhmn">Your medicines will be delivered to your selected address.</p></div>
                  </div>
                  <div className="mt-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4" data-id="element-8376" data-mp="qmcwq995sjhv">
                    <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-8377" data-mp="vko8ipj0l42q" />
                    <div className="min-w-0 flex-1" data-id="element-8378" data-mp="7a70uvqhh68z"><span className="text-xs font-bold text-brand" data-id="element-8379" data-mp="77b7zltht7re">DELIVERY ADDRESS</span><p className="mt-1 font-medium text-gray-900" data-id="element-8380" data-mp="2c5hsat256xi">Riyadh, Al Malqa District, King Fahd Road</p><p className="mt-1 text-sm text-gray-600" data-id="element-8381" data-mp="g2sha56n70un">Ahmed Mohammed · +966 50 123 4567</p></div>
                    <button type="button" onClick={() => setLocationModalOpen(true)} className="shrink-0 text-sm font-bold text-brand transition-colors duration-150 hover:text-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-8382" data-mp="b9ppo2lfe1h3">Change</button>
                  </div>
                </section>
                <section id="section-beneficiary" className="scroll-mt-32 rounded-xl border border-[#d9e5f2] bg-white p-5 shadow-sm" aria-labelledby="beneficiary-title" data-id="element-8383" data-mp="yyink4llyjec">
                  <div data-id="element-8384" data-mp="30sqakej4a00n"><p className="text-xs font-bold uppercase tracking-[0.12em] text-[#256a88]" data-id="element-8385" data-mp="30sq8m8zk3py0">Before you upload</p><h2 id="beneficiary-title" className="mt-1 text-xl font-bold text-gray-900" data-id="element-8386" data-mp="2ws7hyi9egpxe">Who is this prescription for?</h2><p className="mt-1 text-sm text-gray-600" data-id="element-8387" data-mp="2mnhl2qsusc01">Select the beneficiary so we can use the right prescription, insurance and allergy profile.</p></div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-8388" data-mp="2cxoant4g62xd">{beneficiaries.map(beneficiary => <button key={beneficiary.id} type="button" onClick={() => setSelectedBeneficiary(beneficiary.id)} className={`rounded-xl border-2 p-4 text-left transition-[border-color,background-color] duration-150 ${selectedBeneficiary === beneficiary.id ? 'border-[#16726c] bg-[#f2fbfa]' : 'border-gray-200 bg-white hover:border-[#b8e2df]'}`} data-id="element-8389" data-mp="2hvg26m16jn11"><span className="block text-sm font-bold text-gray-900" data-id="element-8390" data-mp="1tzc1469mir19">{beneficiary.name}</span><span className="mt-1 block text-xs text-gray-600" data-id="element-8391" data-mp="1ywbvhjx425cj">{beneficiary.detail}</span>{selectedBeneficiary === beneficiary.id && <span className="mt-3 inline-flex rounded-full bg-[#16726c] px-2 py-1 text-[10px] font-bold text-white" data-id="element-8392" data-mp="3odczkq19hc0t">Selected</span>}</button>)}</div>
                  <button type="button" onClick={() => alert('Add family members from RX Health, then they will appear here.')} className="mt-4 text-sm font-bold text-[#16726c]" data-id="element-8393" data-mp="10pwtumun1592">+ Add a family member</button>
                </section>
                {/* Prescription Type */}
                <div id="section-prescription-type" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-8394" data-mp="qpnxn90nbjto">
                  <div className="flex items-center justify-between gap-3" data-id="element-8395" data-mp="1jgxstavrd0sm"><h2 className="text-xl font-bold" data-id="element-8396" data-mp="1og10l334hl4a">Prescription Type</h2>{selectedBeneficiary && <span className="rounded-full bg-[#f2fbfa] px-3 py-1 text-xs font-bold text-[#16726c]" data-id="element-8397" data-mp="1afq9byxwi4il">For {beneficiaries.find(item => item.id === selectedBeneficiary)?.name}</span>}</div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-8398" data-mp="1ej63momhzss4">
                    {[{
                    id: 'cash',
                    label: 'Cash Prescription',
                    icon: BanknoteIcon
                  }, {
                    id: 'wasfaty',
                    label: 'Wasfaty',
                    icon: FileTextIcon
                  }, {
                    id: 'insurance',
                    label: 'Insurance',
                    icon: WalletIcon
                  }, {
                    id: 'outpatient',
                    label: 'Clinic prescription',
                    icon: HomeIcon
                  }, {
                    id: 'yusr',
                    label: 'Yusr',
                    icon: CheckCircleIcon
                  }].map(type => <button key={type.id} onClick={() => setSelectedPrescriptionType(type.id)} className={`border-2 rounded-lg p-4 text-center transition-[border-color,background-color] duration-150 ${selectedPrescriptionType === type.id ? 'border-brand bg-brand-light' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8399" data-mp="2q6imgp2in3sg">
                        <type.icon className={`w-8 h-8 mx-auto mb-2 ${selectedPrescriptionType === type.id ? 'text-brand' : 'text-gray-400'}`} data-id="element-8400" data-mp="3jpg3nelg2cg0" />
                        <span className="text-sm font-medium" data-id="element-8401" data-mp="2ry0hflnsurqb">
                          {type.label}
                        </span>
                      </button>)}
                  </div>
                  {/* Wasfaty Additional Fields */}
                  {selectedPrescriptionType === 'wasfaty' && <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-8402" data-mp="35yl2koh6szmz">
                      <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-8403" data-mp="g6wondrs1335">
                        Wasfaty Information
                      </h3>
                      <div className="space-y-4" data-id="element-8404" data-mp="25otr9xjp15m3">
                        <div data-id="element-8405" data-mp="25nigvevqszm0">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8406" data-mp="25qo3mngwfpek">
                            Customer Name{' '}
                            <span className="text-red-500" data-id="element-8407" data-mp="1vy8lnq5lr0to">*</span>
                          </label>
                          <input type="text" value={wasfatyCustomerName} onChange={e => setWasfatyCustomerName(e.target.value)} placeholder="Enter customer name" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8408" data-mp="1r0p86sgqas2s" />
                        </div>
                        <div data-id="element-8409" data-mp="37c2w9rlnhg20">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8410" data-mp="32fcacnn5zq8o">
                            National ID <span className="text-red-500" data-id="element-8411" data-mp="3af8tmcv7gmuc">*</span>
                          </label>
                          <input type="text" value={wasfatyNationalId} onChange={e => setWasfatyNationalId(e.target.value)} placeholder="Enter national ID" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8412" data-mp="3ffemfwd7ofe0" />
                        </div>
                        <div data-id="element-8413" data-mp="2hsxr2wo1gbuw">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8414" data-mp="2mq7gicbfgniq">
                            Wasfaty Number{' '}
                            <span className="text-red-500" data-id="element-8415" data-mp="28rhkl00cgtvy">*</span>
                          </label>
                          <input type="text" value={wasfatyNumber} onChange={e => setWasfatyNumber(e.target.value)} placeholder="Enter Wasfaty number" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8416" data-mp="2cqyqtvnlhv26" />
                        </div>
                      </div>
                    </div>}
                  {selectedPrescriptionType === 'outpatient' && <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-4" data-id="element-8417" data-mp="2838r0mw4s36n"><h3 className="mb-2 text-sm font-bold text-gray-900" data-id="element-8418" data-mp="2ltubuh55an76">Outpatient clinic information</h3><p className="mb-4 text-xs leading-5 text-gray-600" data-id="element-8419" data-mp="2ht1zvg4w2477">Add the clinic name and upload the prescription for pharmacist review.</p><label className="block text-sm font-medium text-gray-700" data-id="element-8420" data-mp="2vtuus1z3r1au">Clinic name <span className="text-red-500" data-id="element-8421" data-mp="2qui9hlnvq2ad">*</span><input type="text" value={outpatientClinic} onChange={event => setOutpatientClinic(event.target.value)} placeholder="Enter clinic or hospital name" className="mt-2 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" data-id="element-8422" data-mp="2qvjkxr8t4qsm" /></label></div>}
                  {selectedPrescriptionType === 'yusr' && <div className="mt-6 rounded-lg border border-violet-200 bg-violet-50 p-4" data-id="element-8423" data-mp="228qx19czrolz"><h3 className="mb-2 text-sm font-bold text-gray-900" data-id="element-8424" data-mp="277ldw5ref33g">Yusr information</h3><p className="mb-4 text-xs leading-5 text-gray-600" data-id="element-8425" data-mp="2c650m8dtgiwv">Enter the Yusr reference shown with your eligible prescription.</p><label className="block text-sm font-medium text-gray-700" data-id="element-8426" data-mp="2g8033g7hh2c8">Yusr reference <span className="text-red-500" data-id="element-8427" data-mp="2g5xd2w6bz97i">*</span><input type="text" value={yusrReference} onChange={event => setYusrReference(event.target.value)} placeholder="Enter Yusr reference" className="mt-2 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" data-id="element-8428" data-mp="2l7366v3wkyhk" /></label></div>}
                  {/* Insurance Additional Fields */}
                  {selectedPrescriptionType === 'insurance' && <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-8429" data-mp="ehsvohwb17so">
                      <h3 className="text-sm font-bold text-gray-900 mb-4" data-id="element-8430" data-mp="9k9htj3iqx19">
                        Insurance Information
                      </h3>
                      <div className="space-y-4" data-id="element-8431" data-mp="12857w4yl1qp2">
                        <div data-id="element-8432" data-mp="126u96jjzv94t">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8433" data-mp="1262inn5981le">
                            Customer Name{' '}
                            <span className="text-red-500" data-id="element-8434" data-mp="s9fyz1onzgky">*</span>
                          </label>
                          <input type="text" value={insuranceCustomerName} onChange={e => setInsuranceCustomerName(e.target.value)} placeholder="Enter customer name" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8435" data-mp="o95l72umrlvj" />
                        </div>
                        <div data-id="element-8436" data-mp="12zn80yt0t0td">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8437" data-mp="17vvgxugmjxs3">
                            National ID <span className="text-red-500" data-id="element-8438" data-mp="1vlg4kqnq3j7p">*</span>
                          </label>
                          <input type="text" value={insuranceNationalId} onChange={e => setInsuranceNationalId(e.target.value)} placeholder="Enter national ID" className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8439" data-mp="1zo31h5z93ubr" />
                        </div>
                        <div data-id="element-8440" data-mp="2so9hdh13rxqt">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8441" data-mp="2op0m27qezrt5">
                            Insurance Provider{' '}
                            <span className="text-red-500" data-id="element-8442" data-mp="32eufl3cv7097">*</span>
                          </label>
                          <select value={insuranceProvider} onChange={e => setInsuranceProvider(e.target.value)} className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8443" data-mp="2xhm4fmq4vwhg">
                            <option value="" data-id="element-8444" data-mp="2esnv3hm97s23">Select insurance provider</option>
                            <option value="bupa" data-id="element-8445" data-mp="3v39nz71hqkbk">Bupa Arabia</option>
                            <option value="tawuniya" data-id="element-8446" data-mp="3q6jamrh2m2tu">Tawuniya</option>
                            <option value="medgulf" data-id="element-8447" data-mp="1j10z4708orct">MedGulf</option>
                            <option value="alrajhi" data-id="element-8448" data-mp="1j2c0k067pn0r">Al Rajhi Takaful</option>
                            <option value="saico" data-id="element-8449" data-mp="1e1669xwwlrp4">SAICO</option>
                            <option value="other" data-id="element-8450" data-mp="1a02ov6z7jnls">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>}
                </div>
                {/* Upload Prescription */}
                <div id="section-upload" className="bg-white rounded-xl shadow-md p-4 md:p-6 transition-all scroll-mt-32" data-id="element-8451" data-mp="21tmp4aogtnph">
                  <h2 className="text-lg md:text-xl font-bold mb-4" data-id="element-8452" data-mp="1sxx5o76kv3eq">
                    Upload Prescription
                  </h2>
                  <aside className="mb-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" aria-label="Prescription upload requirements" data-id="element-8453" data-mp="1nyav65i4w19m">
                    <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" data-id="element-8454" data-mp="2gnriwput6tzi" />
                    <div data-id="element-8455" data-mp="2o0z5ngmr344x">
                      <h3 className="text-sm font-bold text-amber-950" data-id="element-8456" data-mp="2o1s29orzwd9c">Before you upload</h3>
                      <ul className="mt-1 space-y-1 text-xs leading-5 text-amber-900" data-id="element-8457" data-mp="2szk3xaifzdqq">
                        <li data-id="element-8458" data-mp="2xxbv22b9ivtv">Make sure the patient name, doctor’s stamp or signature, and issue date are clearly visible.</li>
                        <li data-id="element-8459" data-mp="31xm4joy9k4dh">Prescriptions are valid for up to 30 days from the issue date.</li>
                        <li data-id="element-8460" data-mp="36wp9l9e8ualg">Controlled medicines cannot be processed through this online service.</li>
                      </ul>
                    </div>
                  </aside>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-8461" data-mp="3gpxsgr1tiugk">
                    {/* Prescription Image - Mobile-first */}
                    <div className="order-1" data-id="element-8462" data-mp="3ppb3ho5lbr3e">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8463" data-mp="3pqm2zbs06cop">
                        Prescription Image
                      </label>
                      {selectedFile ?
                    // Inline preview
                    <div className="relative border-2 border-[#1E2B55] rounded-lg overflow-hidden bg-gray-50" data-id="element-8464" data-mp="1ujs8slmmyg4t">
                          {selectedFile.type.startsWith('image/') ? <img src={URL.createObjectURL(selectedFile)} alt="Prescription preview" className="w-full h-48 md:h-56 object-contain bg-white" data-id="element-8465" data-mp="24e0sqkxlp0rd" /> : <div className="w-full h-48 md:h-56 flex flex-col items-center justify-center bg-white" data-id="element-8466" data-mp="29ddet3gne3sw">
                              <FileTextIcon className="w-12 h-12 text-[#1E2B55] mb-2" data-id="element-8467" data-mp="2ibyyj1xuvov9" />
                              <p className="text-sm font-medium text-gray-900 px-4 text-center break-all" data-id="element-8468" data-mp="swssmbtzm6t0">
                                {selectedFile.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-8469" data-mp="o0291eiz4t2u">
                                {(selectedFile.size / 1024).toFixed(0)} KB
                              </p>
                            </div>}
                          <button onClick={() => setSelectedFile(null)} className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-600 rounded-full p-1.5 shadow-md transition-all" aria-label="Remove file" data-id="element-8470" data-mp="2qrogu2in1mpa">
                            <XCircleIcon className="w-5 h-5" data-id="element-8471" data-mp="2luy0de2x8p3c" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1" data-id="element-8472" data-mp="27vokygmjhz0f">
                            <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-8473" data-mp="628tximjii4x" />
                            Uploaded
                          </div>
                        </div> : <>
                          {/* Mobile: Two big action buttons */}
                          <div className="md:hidden grid grid-cols-2 gap-3" data-id="element-8474" data-mp="3cj7o9wdr75cb">
                            <input type="file" accept="image/*" capture="environment" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-camera" data-id="element-8475" data-mp="37ko0dqflnjhb" />
                            <label htmlFor="prescription-camera" className="flex flex-col items-center justify-center gap-2 bg-[#1E2B55] active:bg-[#162041] text-white font-semibold py-5 rounded-lg cursor-pointer transition-colors" data-id="element-8476" data-mp="2ouocv7zeqcg0">
                              <ImageIcon className="w-7 h-7" data-id="element-8477" data-mp="2awrgabkye61c" />
                              <span className="text-sm" data-id="element-8478" data-mp="qbl1507681du">Take Photo</span>
                            </label>
                            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-upload" data-id="element-8479" data-mp="ucdbjwrs4cbp" />
                            <label htmlFor="prescription-upload" className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-[#1E2B55] active:bg-blue-50 text-[#1E2B55] font-semibold py-5 rounded-lg cursor-pointer transition-colors" data-id="element-8480" data-mp="bl3yxempp7fw">
                              <FileTextIcon className="w-7 h-7" data-id="element-8481" data-mp="3p1be4kwr8gib" />
                              <span className="text-sm" data-id="element-8482" data-mp="3h1ninimdk9mm">Choose File</span>
                            </label>
                          </div>
                          {/* Desktop: Drop zone */}
                          <div className="hidden md:block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1E2B55] transition-colors" data-id="element-8483" data-mp="3qrgumkqfiqre">
                            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-8484" data-mp="3lt5l6b41qi9p" />
                            <p className="text-sm text-gray-600 mb-2" data-id="element-8485" data-mp="2y3b93vrjwxqi">
                              Choose file or drag and drop
                            </p>
                            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" id="prescription-upload-desktop" data-id="element-8486" data-mp="381id7e0qrlb3" />
                            <label htmlFor="prescription-upload-desktop" className="inline-block bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-2 px-4 rounded-lg cursor-pointer transition-colors" data-id="element-8487" data-mp="207qh5sb5ydu8">
                              Browse Files
                            </label>
                            <p className="text-xs text-gray-500 mt-3" data-id="element-8488" data-mp="2jykgn6vrxryv">
                              Supported: JPG, PNG, PDF • Max 5 MB
                            </p>
                          </div>
                        </>}
                      <p className="text-[11px] text-gray-500 mt-2 md:hidden" data-id="element-8489" data-mp="2f6kgbdbskx1r">
                        JPG, PNG, PDF • Max 5 MB
                      </p>
                    </div>
                    {/* Prescription Details */}
                    <div className="order-2" data-id="element-8490" data-mp="37n3jv7zyvek3">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8491" data-mp="37oy9gzm6nuui">
                        Prescription Details{' '}
                        <span className="text-gray-400 font-normal" data-id="element-8492" data-mp="12tdm0rzxkdao">
                          (optional)
                        </span>
                      </label>
                      <textarea value={prescriptionDetails} onChange={e => setPrescriptionDetails(e.target.value)} placeholder="Medication names, dosage, quantity…" rows={4} className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent text-base" data-id="element-8493" data-mp="xsg7cpe4q1dx" />
                    </div>
                  </div>
                </div>
                {/* Payment Method */}
                <div id="section-payment" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-8494" data-mp="57on15c6ibiw">
                  <h2 className="text-xl font-bold mb-4" data-id="element-8495" data-mp="2csojoykcijb6">
                    Select Payment Method
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-8496" data-mp="2gu0ah7eyhaky">
                    {[{
                    id: 'paytabs',
                    label: 'PayTabs / Tabby'
                  }, {
                    id: 'mada',
                    label: 'Mada'
                  }, {
                    id: 'stcpay',
                    label: 'STCPay'
                  }, {
                    id: 'credit-card',
                    label: 'Credit Card'
                  }, {
                    id: 'cash',
                    label: 'Cash on Delivery'
                  }].map(method => <button key={method.id} onClick={() => setSelectedPaymentMethod(method.id)} className={`border-2 rounded-lg p-4 text-center transition-all ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8497" data-mp="1f4n1kvf3o14r">
                        <CreditCardIcon className={`w-8 h-8 mx-auto mb-2 ${selectedPaymentMethod === method.id ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-8498" data-mp="25q8hpnmxfaj5" />
                        <span className="text-sm font-medium" data-id="element-8499" data-mp="1d3npkkymmuej">
                          {method.label}
                        </span>
                      </button>)}
                  </div>
                </div>
                {/* Out-of-Stock Handling */}
                <div id="section-out-of-stock" className="bg-white rounded-xl shadow-md p-6 transition-all scroll-mt-32" data-id="element-8500" data-mp="pdjn4gx6d0p4">
                  <h2 className="text-xl font-bold mb-4" data-id="element-8501" data-mp="u9s13qzmynup">
                    If the product is not available
                  </h2>
                  <div className="space-y-3" data-id="element-8502" data-mp="33kj6woliny2g">
                    {[{
                    id: 'contact',
                    label: 'Contact me by phone'
                  }, {
                    id: 'no-replace',
                    label: 'Do not replace missing items'
                  }, {
                    id: 'alternative',
                    label: 'Choose an alternative if available'
                  }, {
                    id: 'complete',
                    label: 'Complete my order with available items only'
                  }].map(option => <label key={option.id} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedOutOfStock === option.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8503" data-mp="25w9447dwp6aa">
                        <input type="radio" name="out-of-stock" value={option.id} checked={selectedOutOfStock === option.id} onChange={e => setSelectedOutOfStock(e.target.value)} className="w-4 h-4 text-[#1E2B55] focus:ring-[#1E2B55]" data-id="element-8504" data-mp="1w7byh53v0paq" />
                        <span className="ml-3 text-sm font-medium" data-id="element-8505" data-mp="217zyuhdzaihm">
                          {option.label}
                        </span>
                      </label>)}
                  </div>
                  <p className="text-xs text-gray-500 mt-4" data-id="element-8506" data-mp="2sye820b1r6my">
                    * Delivery charges will apply if the total is below 15 SAR.
                  </p>
                </div>
                {/* Submit / Next-Action Button (Desktop inline) */}
                {(() => {
                const nextStep = getNextRequiredStep();
                const ready = !nextStep;
                return <button onClick={e => {
                  if (isSubmitting) return;
                  if (ready) {
                    handleSubmit(e);
                  } else if (nextStep) {
                    scrollToSection(nextStep.sectionId);
                  }
                }} disabled={isSubmitting} className="hidden md:flex w-full py-4 rounded-lg font-bold text-lg transition-all bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white items-center justify-center gap-2 disabled:opacity-70" aria-label={ready ? 'Submit prescription request' : `Go to ${nextStep!.label}`} data-id="element-8507" data-mp="w3b3loho2up2">
                      {isSubmitting ? <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8508" data-mp="2adkk6mspsjj">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8509" data-mp="c6gwg47g95jo"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8510" data-mp="uzay5wg2b15y"></path>
                          </svg>
                          Sending now
                        </> : ready ? <>
                          <CheckCircleIcon className="w-5 h-5" data-id="element-8511" data-mp="105pvc6012kyt" />
                          Submit Request
                        </> : <>
                          {nextStep!.label}
                          <ChevronRightIcon className="w-5 h-5" data-id="element-8512" data-mp="1nt7gvjsp8mtc" />
                        </>}
                    </button>;
              })()}
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-8513" data-mp="1ez2wnj82m9ms">
          <Footer data-id="element-8514" data-mp="2bjledpu1zdvb" />
        </div>
        {/* Mobile Sticky Next-Action / Submit Button */}
        {(() => {
        const nextStep = getNextRequiredStep();
        const ready = !nextStep;
        return <div className="md:hidden fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-8515" data-mp="4vufur12vc2e">
              <button onClick={e => {
            if (isSubmitting) return;
            if (ready) {
              handleSubmit(e as unknown as React.FormEvent);
            } else if (nextStep) {
              scrollToSection(nextStep.sectionId);
            }
          }} disabled={isSubmitting} className="w-full py-3.5 rounded-lg font-bold text-base transition-all bg-[#1E2B55] active:bg-[#162041] text-white flex items-center justify-center gap-2 disabled:opacity-70" aria-label={ready ? 'Submit prescription request' : `Go to ${nextStep!.label}`} data-id="element-8516" data-mp="3radtumm54p8c">
                {isSubmitting ? <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8517" data-mp="2w10sjj9t6f84">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8518" data-mp="35s54ea46tjyq"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8519" data-mp="3ap51vwmz1ee8"></path>
                    </svg>
                    Sending now
                  </> : ready ? <>
                    <CheckCircleIcon className="w-5 h-5" data-id="element-8520" data-mp="1peeps9ffzdau" />
                    Submit Request
                  </> : <>
                    {nextStep!.label}
                    <ChevronRightIcon className="w-5 h-5" data-id="element-8521" data-mp="18hg7fgm64co2" />
                  </>}
              </button>
            </div>;
      })()}
        <MobileFooterNav data-id="element-8522" data-mp="1i8ax6uymhue9" />
        <PharmacistChat data-id="element-8523" data-mp="1i7j1zl8xtyoi" />
        {/* Spacer for sticky bottom action + mobile footer nav */}
        <div className="h-32 md:hidden" data-id="element-8524" data-mp="klvg3ua3yyr8"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-8525" data-mp="pjn9ytubcuy2" />
        {/* Prescription Details Modal */}
        {selectedPrescription && <PrescriptionDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} prescription={selectedPrescription} onCancelRequest={requestReference => {
        setReferenceNumber(requestReference);
        setIsModalOpen(false);
        setCancelSheetOpen(true);
      }} data-id="element-8526" data-mp="25xn39lynlmcc" />}
        <PrescriptionSuccessModal isOpen={showSuccessModal} referenceNumber={referenceNumber} fulfillmentMethod={fulfillmentMethod} onClose={() => setShowSuccessModal(false)} onViewStatus={() => {
        setShowSuccessModal(false);
        navigate('/prescription/history');
      }} onCancel={() => {
        setShowSuccessModal(false);
        setCancelSheetOpen(true);
      }} data-id="element-8527" data-mp="3lofm8spy9fu4" />
        <PrescriptionCancelBottomSheet isOpen={cancelSheetOpen} referenceNumber={referenceNumber} onClose={() => setCancelSheetOpen(false)} onConfirm={() => {
        setCancelSheetOpen(false);
        toast.success('Your prescription request has been cancelled.');
        navigate('/prescription/history');
      }} data-id="element-8528" data-mp="2xrs40ygkhzhd" />
      </div>
    </div>;
}